import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import {
  ONLINE_BOOKING_ADDON_BILLING_LABELS,
  type OnlineBookingAddonBilling,
  type OnlineBookingUpsellingSettings,
} from "@/lib/onlineBooking";
import { CreateArrivalTypeModal } from "@/pages/stammdaten/CreateArrivalTypeModal";
import { EditArrivalTypeModal } from "@/pages/stammdaten/EditArrivalTypeModal";
import { OnlineBookingFormHint } from "@/pages/stammdaten/online-booking/OnlineBookingFormHint";
import type { OnlineBookingAngebotSub } from "@/pages/stammdaten/online-booking/onlineBookingSubSections";
import type { OnlineBookingSettings } from "@/pages/stammdaten/online-booking/useOnlineBookingSettings";
import type { ArrivalTypeRecord } from "@/types/camping";

const BILLING_OPTIONS = Object.keys(
  ONLINE_BOOKING_ADDON_BILLING_LABELS,
) as OnlineBookingAddonBilling[];

type Props = Pick<
  OnlineBookingSettings,
  | "upselling"
  | "setUpselling"
  | "addons"
  | "setAddons"
  | "availableArticlesForAddon"
  | "loadUpsellingTemplateSuggestions"
  | "touristIntegrationEnabled"
> & {
  canWrite: boolean;
  subSection: OnlineBookingAngebotSub;
  arrivalTypes: ArrivalTypeRecord[];
  arrivalTypesLoading: boolean;
  onArrivalTypesReload: () => void;
};

export function OnlineBookingAngebotPanel({
  canWrite,
  subSection,
  upselling,
  setUpselling,
  addons,
  setAddons,
  availableArticlesForAddon,
  loadUpsellingTemplateSuggestions,
  touristIntegrationEnabled,
  arrivalTypes,
  arrivalTypesLoading,
  onArrivalTypesReload,
}: Props) {
  const [selectedArrivalType, setSelectedArrivalType] = useState<ArrivalTypeRecord | null>(null);
  const [createArrivalOpen, setCreateArrivalOpen] = useState(false);
  const [addonArticleId, setAddonArticleId] = useState("");
  const [addonBilling, setAddonBilling] = useState<OnlineBookingAddonBilling>("once");

  function onAddAddon() {
    if (!addonArticleId) return;
    const article = availableArticlesForAddon.find((a) => a.id === addonArticleId);
    if (!article) return;
    setAddons((prev) => [
      ...prev,
      {
        article_id: article.id,
        sort_order: prev.length,
        billing: addonBilling,
        label: null,
        article_number: article.article_number,
        article_name: article.name,
        unit: article.unit,
        is_active: article.is_active,
      },
    ]);
    setAddonArticleId("");
    setAddonBilling("once");
  }

  function onRemoveAddon(articleId: string) {
    setAddons((prev) =>
      prev
        .filter((a) => a.article_id !== articleId)
        .map((a, idx) => ({ ...a, sort_order: idx })),
    );
  }

  if (subSection === "anreisearten") {
    return (
      <>
        <EditArrivalTypeModal
          open={!!selectedArrivalType}
          arrivalType={selectedArrivalType}
          onClose={() => setSelectedArrivalType(null)}
          onSaved={onArrivalTypesReload}
          canEdit={canWrite}
          touristIntegrationEnabled={touristIntegrationEnabled}
        />
        <CreateArrivalTypeModal
          open={createArrivalOpen}
          onClose={() => setCreateArrivalOpen(false)}
          onCreated={onArrivalTypesReload}
        />
        <Card
          title="Anreisearten"
          description="Welche Anreisearten Gäste auf der Buchungswebsite wählen können."
          bodyClassName="space-y-4"
        >
          <OnlineBookingFormHint>
            Klicken Sie auf eine Zeile, um die Anreiseart zu bearbeiten. Aktivieren Sie dort
            „In Online-Buchung wählbar“ und legen Sie optional Fahrzeugfelder fest. Mindestens
            eine Anreiseart muss für die Online-Buchung freigegeben sein.
          </OnlineBookingFormHint>
          {canWrite ? (
            <div className="flex justify-end">
              <Button type="button" variant="secondary" onClick={() => setCreateArrivalOpen(true)}>
                Neue Anreiseart
              </Button>
            </div>
          ) : null}
          {arrivalTypesLoading ? (
            <p className="text-sm text-label-secondary">Laden…</p>
          ) : (
            <Table wide emptyMessage="Keine Anreisearten." isEmpty={arrivalTypes.length === 0}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Sortierung</th>
                  <th>Fahrzeuglänge</th>
                  <th>Online-Buchung</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {arrivalTypes.map((t) => (
                  <ClickableTableRow
                    key={t.id}
                    ariaLabel={`Anreiseart ${t.name} öffnen`}
                    onActivate={() => setSelectedArrivalType(t)}
                  >
                    <td className="font-medium">{t.name}</td>
                    <td className="font-mono text-sm text-label-secondary">{t.code}</td>
                    <td>{t.sort_order}</td>
                    <td>{t.requires_vehicle_length ? "Pflicht" : "—"}</td>
                    <td>{t.online_booking_enabled ? "Ja" : "Nein"}</td>
                    <td>{t.is_active ? "Aktiv" : "Inaktiv"}</td>
                  </ClickableTableRow>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </>
    );
  }

  if (subSection === "extras") {
    return (
      <Card
        title="Zusatzartikel"
        description="Optionale Extras, die Gäste während der Buchung dazubuchen können."
        bodyClassName="space-y-4"
      >
        <OnlineBookingFormHint>
          Hier wählen Sie nur, welche Artikel in der Buchungsstrecke angeboten werden und wie sie
          abgerechnet werden (einmalig, pro Nacht, pro Person).
        </OnlineBookingFormHint>
        {addons.length > 0 ? (
          <Table wide isEmpty={false}>
            <thead>
              <tr>
                <th>Artikel</th>
                <th>Abrechnung</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {addons.map((addon) => (
                <tr key={addon.article_id} className="border-b border-separator/60">
                  <td className="py-2">
                    <span className="font-medium">{addon.article_name ?? addon.article_id}</span>
                    {addon.article_number ? (
                      <span className="ml-2 font-mono text-xs text-label-secondary">
                        {addon.article_number}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-2 text-sm">
                    {canWrite ? (
                      <select
                        className="eh-input"
                        value={addon.billing}
                        onChange={(e) => {
                          const billing = e.target.value as OnlineBookingAddonBilling;
                          setAddons((prev) =>
                            prev.map((a) =>
                              a.article_id === addon.article_id ? { ...a, billing } : a,
                            ),
                          );
                        }}
                      >
                        {BILLING_OPTIONS.map((b) => (
                          <option key={b} value={b}>
                            {ONLINE_BOOKING_ADDON_BILLING_LABELS[b]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      ONLINE_BOOKING_ADDON_BILLING_LABELS[addon.billing]
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {canWrite ? (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => onRemoveAddon(addon.article_id)}
                      >
                        Entfernen
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-sm text-label-secondary">Noch keine Zusatzartikel hinterlegt.</p>
        )}
        {canWrite ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <label className="flex flex-1 flex-col gap-1 text-sm">
              Artikel
              <select
                className="eh-input"
                value={addonArticleId}
                onChange={(e) => setAddonArticleId(e.target.value)}
              >
                <option value="">— Artikel wählen —</option>
                {availableArticlesForAddon.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.article_number} – {a.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm sm:w-56">
              Abrechnung
              <select
                className="eh-input"
                value={addonBilling}
                onChange={(e) => setAddonBilling(e.target.value as OnlineBookingAddonBilling)}
              >
                {BILLING_OPTIONS.map((b) => (
                  <option key={b} value={b}>
                    {ONLINE_BOOKING_ADDON_BILLING_LABELS[b]}
                  </option>
                ))}
              </select>
            </label>
            <Button
              type="button"
              variant="secondary"
              disabled={!addonArticleId}
              onClick={onAddAddon}
            >
              Hinzufügen
            </Button>
          </div>
        ) : null}
      </Card>
    );
  }

  return (
    <Card
      title="Upselling & Kategorieauswahl"
      description="Globale Darstellung der Platzkategorien auf der Buchungswebsite."
      bodyClassName="space-y-4"
    >
      <OnlineBookingFormHint>
        Hier steuern Sie die globalen Darstellungsregeln. Pro-Kategorie-Texte und Badges pflegen
        Sie unter Stammdaten → Platzkategorien.
      </OnlineBookingFormHint>
      <fieldset className="space-y-3" disabled={!canWrite}>
        <UpsellingToggles upselling={upselling} setUpselling={setUpselling} />
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-separator pt-3">
          <p className="text-sm font-medium">Preisvergleichs-Textvorlagen</p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => void loadUpsellingTemplateSuggestions()}
          >
            Standardvorlagen laden
          </Button>
        </div>
        <UpsellingTemplates upselling={upselling} setUpselling={setUpselling} />
      </fieldset>
    </Card>
  );
}

function UpsellingToggles({
  upselling,
  setUpselling,
}: {
  upselling: OnlineBookingUpsellingSettings;
  setUpselling: OnlineBookingSettings["setUpselling"];
}) {
  return (
    <>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={upselling.upselling_enabled}
          onChange={(e) => setUpselling((u) => ({ ...u, upselling_enabled: e.target.checked }))}
        />
        Upselling aktiv
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={upselling.auto_highlight_recommended}
          onChange={(e) =>
            setUpselling((u) => ({ ...u, auto_highlight_recommended: e.target.checked }))
          }
        />
        Empfohlene Kategorie optisch hervorheben
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={upselling.auto_select_best_available}
          onChange={(e) =>
            setUpselling((u) => ({ ...u, auto_select_best_available: e.target.checked }))
          }
        />
        Beste verfügbare Kategorie vorauswählen (nach Priorität)
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={upselling.show_price_comparison}
          onChange={(e) =>
            setUpselling((u) => ({ ...u, show_price_comparison: e.target.checked }))
          }
        />
        Preisvergleich anzeigen
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={upselling.show_category_images}
          onChange={(e) =>
            setUpselling((u) => ({ ...u, show_category_images: e.target.checked }))
          }
        />
        Bilder in Kategorieauswahl
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={upselling.show_marketing_texts}
          onChange={(e) =>
            setUpselling((u) => ({ ...u, show_marketing_texts: e.target.checked }))
          }
        />
        Werbetexte anzeigen
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={upselling.show_badges}
          onChange={(e) => setUpselling((u) => ({ ...u, show_badges: e.target.checked }))}
        />
        Badges anzeigen
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Sortierung
        <select
          className="eh-input max-w-xs"
          value={upselling.sort_mode}
          onChange={(e) =>
            setUpselling((u) => ({
              ...u,
              sort_mode: e.target.value as OnlineBookingUpsellingSettings["sort_mode"],
            }))
          }
        >
          <option value="priority">Nach Priorität</option>
          <option value="price">Nach Preis (günstig zuerst)</option>
        </select>
      </label>
      <div className="grid gap-3 lg:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          Standard: Preisvergleich – Bezug
          <select
            className="eh-input"
            value={upselling.price_comparison_reference}
            onChange={(e) =>
              setUpselling((u) => ({
                ...u,
                price_comparison_reference: e.target
                  .value as OnlineBookingUpsellingSettings["price_comparison_reference"],
              }))
            }
          >
            <option value="recommended">Empfohlene Kategorie</option>
            <option value="most_expensive">Teuerste Kategorie</option>
            <option value="selected">Gewählte Kategorie</option>
            <option value="hide">Ausblenden</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Standard: Preisvergleich – Darstellung
          <select
            className="eh-input"
            value={upselling.price_comparison_basis}
            onChange={(e) =>
              setUpselling((u) => ({
                ...u,
                price_comparison_basis: e.target
                  .value as OnlineBookingUpsellingSettings["price_comparison_basis"],
              }))
            }
          >
            <option value="per_night">Differenz pro Nacht</option>
            <option value="total_stay">Gesamtaufenthalt</option>
            <option value="hide">Ausblenden</option>
          </select>
        </label>
      </div>
    </>
  );
}

function UpsellingTemplates({
  upselling,
  setUpselling,
}: {
  upselling: OnlineBookingUpsellingSettings;
  setUpselling: OnlineBookingSettings["setUpselling"];
}) {
  return (
    <>
      <label className="flex flex-col gap-1 text-sm">
        Textvorlage Upgrade (Deutsch)
        <textarea
          className="eh-input min-h-[3rem] text-xs"
          value={upselling.template_upgrade}
          onChange={(e) => setUpselling((u) => ({ ...u, template_upgrade: e.target.value }))}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Textvorlage Upgrade (Englisch)
        <textarea
          className="eh-input min-h-[3rem] text-xs"
          value={upselling.template_upgrade_en ?? ""}
          onChange={(e) => setUpselling((u) => ({ ...u, template_upgrade_en: e.target.value }))}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Textvorlage günstiger (Deutsch)
        <textarea
          className="eh-input min-h-[3rem] text-xs"
          value={upselling.template_cheaper}
          onChange={(e) => setUpselling((u) => ({ ...u, template_cheaper: e.target.value }))}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Textvorlage günstiger (Englisch)
        <textarea
          className="eh-input min-h-[3rem] text-xs"
          value={upselling.template_cheaper_en ?? ""}
          onChange={(e) => setUpselling((u) => ({ ...u, template_cheaper_en: e.target.value }))}
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Textvorlage Gesamtdifferenz (Deutsch)
        <textarea
          className="eh-input min-h-[3rem] text-xs"
          value={upselling.template_total_difference}
          onChange={(e) =>
            setUpselling((u) => ({ ...u, template_total_difference: e.target.value }))
          }
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        Textvorlage Gesamtdifferenz (Englisch)
        <textarea
          className="eh-input min-h-[3rem] text-xs"
          value={upselling.template_total_difference_en ?? ""}
          onChange={(e) =>
            setUpselling((u) => ({ ...u, template_total_difference_en: e.target.value }))
          }
        />
      </label>
      <p className="text-xs text-label-secondary">
        Platzhalter:{" "}
        {(upselling.template_placeholders ?? [
          "category_name",
          "reference_category_name",
          "price_difference_per_night",
          "total_difference",
          "nights",
        ])
          .map((p) => `{${p}}`)
          .join(", ")}
      </p>
    </>
  );
}
